import { ComboboxActionType } from './Combobox.constants';
export const singleComboboxReducer = (state, action) => {
    switch (action.type) {
        case ComboboxActionType.BLUR: {
            const option = state.options.find((option) => option.id === state.innerValue);
            return {
                ...state,
                currentOptionIndex: -1,
                displayValue: option?.text ?? '',
                hasOptions: Boolean(state.options.length),
                isExpanded: false,
                isFocused: false,
                search: '',
            };
        }
        case ComboboxActionType.COLLAPSE: {
            return {
                ...state,
                currentOptionIndex: -1,
                isExpanded: false,
            };
        }
        case ComboboxActionType.ENTER_KEY_PRESS: {
            if (state.isExpanded) {
                const option = state.filteredOptions[state.currentOptionIndex];
                if (option !== undefined) {
                    if (option.disabled) {
                        return state;
                    }
                    return {
                        ...state,
                        currentOptionIndex: -1,
                        displayValue: option.text,
                        hasOptions: Boolean(state.options.length),
                        innerValue: option.id,
                        isExpanded: false,
                        search: '',
                    };
                }
                return {
                    ...state,
                    displayValue: '',
                    hasOptions: Boolean(state.options.length),
                    innerValue: undefined,
                    isExpanded: true,
                    search: '',
                };
            }
            return {
                ...state,
                filteredOptions: state.search
                    ? state.filteredOptions
                    : state.options,
                isExpanded: true,
            };
        }
        case ComboboxActionType.EXPAND: {
            return {
                ...state,
                filteredOptions: state.search
                    ? state.filteredOptions
                    : state.options,
                isExpanded: true,
                isFocused: true,
            };
        }
        case ComboboxActionType.FOCUS: {
            return {
                ...state,
                isFocused: true,
            };
        }
        case ComboboxActionType.OPTIONS_DOWN: {
            let newCurrentOptionIndex = state.currentOptionIndex + 1;
            if (newCurrentOptionIndex > state.filteredOptions.length - 1) {
                newCurrentOptionIndex = 0;
            }
            return {
                ...state,
                currentOptionIndex: newCurrentOptionIndex,
            };
        }
        case ComboboxActionType.OPTIONS_UP: {
            let newCurrentOptionIndex = state.currentOptionIndex - 1;
            if (newCurrentOptionIndex < 0) {
                newCurrentOptionIndex = state.filteredOptions.length - 1;
            }
            return {
                ...state,
                currentOptionIndex: newCurrentOptionIndex,
            };
        }
        case ComboboxActionType.OPTION_MOUSE_CLICK: {
            const option = action.value;
            return {
                ...state,
                currentOptionIndex: -1,
                displayValue: option?.text || '',
                hasOptions: Boolean(state.options.length),
                innerValue: option !== undefined ? option.id : undefined,
                isExpanded: false,
                search: '',
            };
        }
        case ComboboxActionType.SEARCH: {
            let searchValue = action.value;
            if (searchValue.indexOf(state.displayValue) === 0) {
                searchValue = searchValue.slice(state.displayValue.length);
            }
            const newFilteredOptions = searchValue
                ? action.searchFn(searchValue, state.options)
                : state.options;
            return {
                ...state,
                currentOptionIndex: searchValue ? 0 : -1,
                displayValue: '',
                filteredOptions: newFilteredOptions,
                hasOptions: Boolean(newFilteredOptions.length),
                isExpanded: true,
                search: searchValue,
            };
        }
        case ComboboxActionType.SET_OPTIONS: {
            const newOptions = action.value;
            const currentOptionIndex = newOptions.findIndex((o) => o.id === state.innerValue);
            const currentOption = newOptions[currentOptionIndex];
            return {
                ...state,
                displayValue: currentOption ? currentOption.text : '',
                filteredOptions: newOptions,
                isExpanded: false,
                search: '',
                innerValue: currentOption ? currentOption.id : undefined,
                hasOptions: Boolean(newOptions.length),
                options: newOptions,
            };
        }
        case ComboboxActionType.SET_VALUE: {
            const newValue = action.value;
            return {
                ...state,
                displayValue: state.options.find((option) => option.id === newValue)
                    ?.text || '',
                filteredOptions: state.options,
                hasOptions: Boolean(state.options.length),
                innerValue: newValue,
                isExpanded: false,
                search: '',
            };
        }
    }
};
export const multiComboboxReducer = (state, action) => {
    switch (action.type) {
        case ComboboxActionType.BACKSPACE_SELECTED_OPTION: {
            const selectedValues = [...state.innerValue];
            if (state.search.length > 0 || selectedValues.length === 0) {
                return state;
            }
            const optionIndexToBeRemoved = state.currentSelectedOptionIndex === -1
                ? selectedValues.length - 1
                : state.currentSelectedOptionIndex;
            selectedValues.splice(optionIndexToBeRemoved, 1);
            const nextSelectedOptionIndex = selectedValues.length > 0 &&
                state.currentSelectedOptionIndex > -1
                ? state.currentSelectedOptionIndex < selectedValues.length
                    ? state.currentSelectedOptionIndex
                    : selectedValues.length - 1
                : -1;
            return {
                ...state,
                innerValue: selectedValues,
                currentSelectedOptionIndex: nextSelectedOptionIndex,
            };
        }
        case ComboboxActionType.BLUR: {
            return {
                ...state,
                currentOptionIndex: -1,
                currentSelectedOptionIndex: -1,
                currentStyledOptionIndex: -1,
                displayValue: '',
                hasOptions: Boolean(state.options.length),
                isExpanded: false,
                isFocused: false,
                search: '',
            };
        }
        case ComboboxActionType.CHANGE_CURRENT_SELECTED_OPTION: {
            const currentSelectedOptionIndex = action.value;
            return {
                ...state,
                currentSelectedOptionIndex: currentSelectedOptionIndex,
            };
        }
        case ComboboxActionType.COLLAPSE: {
            return {
                ...state,
                currentOptionIndex: -1,
                currentStyledOptionIndex: -1,
                isExpanded: false,
            };
        }
        case ComboboxActionType.DELETE_SELECTED_OPTION: {
            const selectedValues = [...state.innerValue];
            if (selectedValues.length === 0 ||
                state.currentSelectedOptionIndex === -1) {
                return state;
            }
            selectedValues.splice(state.currentSelectedOptionIndex, 1);
            const nextSelectedOptionIndex = selectedValues.length > 0
                ? state.currentSelectedOptionIndex < selectedValues.length
                    ? state.currentSelectedOptionIndex
                    : selectedValues.length - 1
                : -1;
            return {
                ...state,
                innerValue: selectedValues,
                currentSelectedOptionIndex: nextSelectedOptionIndex,
            };
        }
        case ComboboxActionType.ENTER_KEY_PRESS: {
            if (state.isExpanded) {
                const option = state.filteredOptions[state.currentOptionIndex];
                const optionIndex = state.options.findIndex((o) => o.id === option?.id);
                if (option !== undefined) {
                    const innerValue = state.innerValue.includes(option.id)
                        ? state.innerValue.filter((optionId) => optionId !== option.id)
                        : [...state.innerValue, option.id];
                    return {
                        ...state,
                        currentOptionIndex: state.search ? -1 : optionIndex,
                        currentStyledOptionIndex: state.search
                            ? -1
                            : optionIndex,
                        displayValue: '',
                        filteredOptions: state.options,
                        hasOptions: Boolean(state.options.length),
                        innerValue,
                        isExpanded: true,
                        search: '',
                    };
                }
                return {
                    ...state,
                    isExpanded: true,
                };
            }
            return {
                ...state,
                filteredOptions: state.search
                    ? state.filteredOptions
                    : state.options,
                isExpanded: true,
            };
        }
        case ComboboxActionType.EXPAND: {
            return {
                ...state,
                filteredOptions: state.search
                    ? state.filteredOptions
                    : state.options,
                isExpanded: true,
                isFocused: true,
            };
        }
        case ComboboxActionType.FOCUS: {
            return {
                ...state,
                isFocused: true,
            };
        }
        case ComboboxActionType.OPTIONS_DOWN: {
            if (state.currentStyledOptionIndex === -1 &&
                state.currentOptionIndex !== -1) {
                return {
                    ...state,
                    currentStyledOptionIndex: state.currentOptionIndex,
                };
            }
            let newCurrentOptionIndex = state.currentOptionIndex + 1;
            if (newCurrentOptionIndex > state.filteredOptions.length - 1) {
                newCurrentOptionIndex = 0;
            }
            return {
                ...state,
                currentOptionIndex: newCurrentOptionIndex,
                currentStyledOptionIndex: newCurrentOptionIndex,
            };
        }
        case ComboboxActionType.OPTIONS_UP: {
            if (state.currentStyledOptionIndex === -1 &&
                state.currentOptionIndex !== -1) {
                return {
                    ...state,
                    currentStyledOptionIndex: state.currentOptionIndex,
                };
            }
            let newCurrentOptionIndex = state.currentOptionIndex - 1;
            if (newCurrentOptionIndex < 0) {
                newCurrentOptionIndex = state.filteredOptions.length - 1;
            }
            return {
                ...state,
                currentOptionIndex: newCurrentOptionIndex,
                currentStyledOptionIndex: newCurrentOptionIndex,
            };
        }
        case ComboboxActionType.OPTION_MOUSE_CLICK: {
            const option = action.value;
            const optionIndex = state.options.findIndex((o) => o.id === option.id);
            const isSelected = state.innerValue.includes(option.id);
            const innerValue = isSelected
                ? state.innerValue.filter((optionId) => optionId !== option.id)
                : [...state.innerValue, option.id];
            return {
                ...state,
                currentOptionIndex: state.search ? -1 : optionIndex,
                currentStyledOptionIndex: isSelected || state.search ? -1 : optionIndex,
                filteredOptions: state.options,
                displayValue: '',
                hasOptions: Boolean(state.options.length),
                innerValue,
                isExpanded: true,
                search: '',
            };
        }
        case ComboboxActionType.SEARCH: {
            let searchValue = action.value;
            if (searchValue.indexOf(state.displayValue) === 0) {
                searchValue = searchValue.slice(state.displayValue.length);
            }
            const newFilteredOptions = searchValue
                ? action.searchFn(searchValue, state.options)
                : state.options;
            return {
                ...state,
                currentOptionIndex: searchValue ? 0 : -1,
                currentStyledOptionIndex: searchValue ? 0 : -1,
                displayValue: '',
                filteredOptions: newFilteredOptions,
                hasOptions: Boolean(newFilteredOptions.length),
                isExpanded: true,
                search: searchValue,
            };
        }
        case ComboboxActionType.SELECTED_OPTIONS_RIGHT: {
            if (state.search.length > 0 ||
                state.innerValue.length === 0 ||
                state.currentSelectedOptionIndex === -1) {
                return state;
            }
            const newCurrentSelectedOptionIndex = state.currentSelectedOptionIndex >= state.innerValue.length - 1
                ? -1
                : state.currentSelectedOptionIndex + 1;
            return {
                ...state,
                currentSelectedOptionIndex: newCurrentSelectedOptionIndex,
            };
        }
        case ComboboxActionType.SELECTED_OPTIONS_LEFT: {
            if (state.search.length > 0 ||
                state.innerValue.length === 0 ||
                state.currentSelectedOptionIndex === 0) {
                return state;
            }
            const newCurrentSelectedOptionIndex = state.currentSelectedOptionIndex === -1
                ? state.innerValue.length - 1
                : state.currentSelectedOptionIndex - 1;
            return {
                ...state,
                currentSelectedOptionIndex: newCurrentSelectedOptionIndex,
            };
        }
        case ComboboxActionType.SET_OPTIONS: {
            const newOptions = action.value;
            const newInnerValue = state.innerValue.filter((optionId) => newOptions.find((o) => o.id === optionId));
            return {
                ...state,
                displayValue: '',
                filteredOptions: newOptions,
                isExpanded: false,
                search: '',
                innerValue: newInnerValue,
                hasOptions: Boolean(newOptions.length),
                options: newOptions,
            };
        }
        case ComboboxActionType.SET_VALUE: {
            const newValue = action.value;
            if (state.innerValue.every((optionId) => newValue.includes(optionId)) &&
                newValue.every((optionId) => state.innerValue.includes(optionId))) {
                return state;
            }
            return {
                ...state,
                displayValue: '',
                hasOptions: Boolean(state.options.length),
                innerValue: newValue,
                isExpanded: Boolean(newValue.length),
                search: '',
            };
        }
    }
};
export const comboboxReducer = (isMultiselect) => isMultiselect
    ? multiComboboxReducer
    : singleComboboxReducer;
